import React from 'react';
import { pathOr } from 'ramda';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { useHistory } from 'react-router';
import Spinner from 'admin-components/components/Spinner';
import { Row, Column } from 'components/shared/Layout';
import { rollbackVoucherPayments } from 'components/ui/Payments/Payments.utils';

import { userShape } from 'constants/propTypes';
import { ACTION_CODES } from 'constants/errors';
import AgentPortalError from 'errors/AgentPortalError';
import { getPendingPaymentURL } from 'utils/urls';
import { useDefaultedCurrencyFromURL } from 'utils/hooks/currency';
import { hasHotel } from 'utils/booking';

import { ROLLBACK_VOUCHER_REDEMPTION } from 'store/mutations';
import PaymentPending from './PaymentPending';
import { GET_BOOKING, CANCEL_PAYMENTS } from './graphql';
import { GET_CUSTOMER_DETAILS } from 'store/queries';

const PaymentPendingContainer = ({ reference, bookingCurrency, isMultiCurrency, user }) => {
  const client = useApolloClient();
  const { location } = useHistory();

  const membershipNumber = new URLSearchParams(location?.search).get('membershipNumber');

  const [currency, changeCurrency] = useDefaultedCurrencyFromURL({
    availableCurrencies: user.paymentCurrencies,
    defaultCurrency: bookingCurrency,
    isMultiCurrency
  });

  const {
    loading: bookingLoading,
    error: bookingError,
    data: bookingData,
    refetch
  } = useQuery(GET_BOOKING, {
    variables: { reference, currency },
    fetchPolicy: 'network-only'
  });

  const booking = pathOr({}, ['booking'], bookingData);

  const [cancelPaymentsMutation, cancelPaymentsState] = useMutation(CANCEL_PAYMENTS, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const [rollbackVoucherRedemption, rollbackVoucherRedemptionState] = useMutation(ROLLBACK_VOUCHER_REDEMPTION);

  const cancelPayments = async () => {
    let customerInfo;

    if (membershipNumber) {
      try {
        const result = await client.query({
          query: GET_CUSTOMER_DETAILS,
          variables: {
            membershipNumber
          }
        });

        customerInfo = result?.data?.getCustomerDetails;
      } catch (e) {
        console.error('Failed to get customer details:', e);
      }
    }

    return Promise.all([
      cancelPaymentsMutation({
        variables: {
          bookingReference: reference,
          currency,
          paymentLocation: user.paymentLocation.name,
          hasHotel: booking.isPackage || hasHotel(booking),
          customerId: customerInfo?.profile?.customerId,
          shouldCancelLoyalty: true
        }
      }),
      rollbackVoucherPayments({
        payments: pathOr([], ['booking', 'payments'], bookingData),
        bookingReference: reference,
        currency,
        rollbackMutation: rollbackVoucherRedemption
      })
    ]);
  };

  const isLoading = bookingLoading || cancelPaymentsState.loading || rollbackVoucherRedemptionState.loading;
  const error = [bookingError, cancelPaymentsState.error, rollbackVoucherRedemptionState.error].find(Boolean);

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: getPendingPaymentURL(reference, currency)
    });
  }

  if (isLoading) {
    return (
      <Row span={16} height="100vh">
        <Column justifyContent="center" alignItems="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  return (
    <PaymentPending
      currency={currency}
      booking={booking}
      onCurrencyChange={changeCurrency}
      paymentCurrencies={user.paymentCurrencies}
      cancelPayments={cancelPayments}
      refetchBooking={refetch}
    />
  );
};

PaymentPendingContainer.propTypes = {
  reference: PropTypes.string.isRequired,
  user: userShape,
  bookingCurrency: PropTypes.string.isRequired,
  isMultiCurrency: PropTypes.bool.isRequired
};

export default PaymentPendingContainer;
